import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller, useFieldArray, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';

import {
  selectFacilityUsers,
  fetchFacilityUsers,
} from '../../../modules/facility';
import {
  createFacilityShift,
  selectFacilityShiftCreateLoading,
  selectFacilityShiftCreateData,
  fetchShiftDetails,
  updateFacilityShift,
  selectFacilityShiftUpdateLoading,
  selectShiftDetailsData,
  selectShiftDetailsLoading,
  resetFacilityShiftCreateState,
  resetFacilityShiftUpdateState,
} from '../../../modules/facilityShifts';
import { selectUserProfile } from '../../../modules/user';
import { fetchFacilityList } from '../../../modules/facility/actions';
import { fetchRoleList, IRole } from '../../../modules/role';
import { useNavigate, useParams } from 'react-router-dom';
import { selectFacilityId } from '../../../modules/facilityProfile';
import PaginatedSearchSelect from '../../../components/PaginatedSearchSelect';
import { OptionType, FetchResponse } from '../../../interfaces/paginatedSearchSelect.interface';
import AlertPopup from '../../../components/common/AlertPopup';
import FacilityShiftWorkerSuggestions from '../../../components/FacilityShiftWorkerSuggestions';

import { useSidebar } from '../../../context/SidebarContext';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';

import {
  Box,
  TextField,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  IconButton,
  Button,
  FormHelperText,
  CircularProgress,
  Backdrop
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  PageWrapper,
  FormContainer,
  HeaderRow,
  SectionLabel,
  THEME_COLOR,
  CancelButton,
  PrimaryButton
} from './styles';

interface ShiftSlotItem {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  isSameDay: boolean;
  selectedWorkers: (OptionType | null)[];
}

interface IFormInput {
  shiftSlots: ShiftSlotItem[];
  position: OptionType | null;
  workerCount: number | string;
  experience: string;
  appointMember: string;
  notes: string;
  isMultiShift: boolean;
  selectedFacility: OptionType | null;
  isSleepover: boolean;
  label: string;
}

// --- YUP VALIDATION SCHEMA ---

const dateValidation = yup.mixed()
  .test('required', 'This field is required', (value: any) => value !== null)
  .test('isValid', 'Invalid Date', (value: any) => {
    if (value && dayjs.isDayjs(value)) {
      return value.isValid();
    }
    return true;
  });

const schema = yup.object({
  shiftSlots: yup.array()
    .of(
      yup.object({
        startDate: dateValidation,
        endDate: dateValidation,
        startTime: dateValidation,
        endTime: dateValidation,
        isSameDay: yup.boolean().default(false)
      }).test('is-future', 'Shift start must be in the future', function (slot) {
        if (!slot.startDate || !slot.startTime) return true;
        const startDate = slot.startDate as any;
        const startTime = slot.startTime as any;
        const start = startDate.hour(startTime.hour()).minute(startTime.minute());
        if (start.isBefore(dayjs())) {
          return this.createError({
            path: `${this.path}.startDate`,
            message: 'Shift must start in the future',
          });
        }
        return true;
      })
    )
    .required()
    .min(1, "At least one shift slot is required"),
  position: yup.mixed<OptionType>().required('Position is required').nullable(),
  workerCount: yup.number().required('Number of workers is required').typeError('Required'),
  experience: yup.string().required('Experience is required'),
  appointMember: yup.string().required('Appoint member is required'),
  notes: yup.string().max(120, 'Max 120 characters allowed').default(''),
  label: yup.string().max(100, 'Max 100 characters allowed').default(''),
  isSleepover: yup.boolean().default(false),
  isMultiShift: yup.boolean().default(false),
  selectedFacility: yup.mixed<OptionType>().nullable().when('$isOrgAdmin', {
    is: true,
    then: (schema) => schema.required('Facility is required'),
    otherwise: (schema) => schema.notRequired()
  }),
});

const defaultValues: IFormInput = {
  shiftSlots: [{ 
    startDate: null, 
    endDate: null, 
    startTime: null, 
    endTime: null,
    isSameDay: true,
    selectedWorkers: []
  }],
  position: null,
  workerCount: '',
  experience: '',
  appointMember: '',
  notes: '',
  isMultiShift: false,
  selectedFacility: null,
  isSleepover: false,
  label: '',
};

const FacilityShiftCreate: React.FC = () => {
  const navigate = useNavigate();
  const { setActivePage } = useSidebar();
  const { setIsShow } = useBreadcrumb();
  useEffect(() => {
    setActivePage('new_shift');
    setIsShow(false);
  }, [setActivePage, setIsShow]);

  const { shiftId: routeShiftId } = useParams<{ shiftId: string }>();
  const [shiftId, setShiftId] = useState<number | null>(routeShiftId ? parseInt(routeShiftId) : null);

  const isPastSlot = useCallback((slot: ShiftSlotItem) => {
    if (!slot.startDate || !slot.startTime) return false;
    const start = slot.startDate.hour(slot.startTime.hour()).minute(slot.startTime.minute());
    return start.isBefore(dayjs());
  }, []);

  const dispatch = useDispatch<any>();

  useEffect(() => {
    return () => {
      dispatch(resetFacilityShiftCreateState());
      dispatch(resetFacilityShiftUpdateState());
    };
  }, [dispatch]);
  const facilityUsers = useSelector(selectFacilityUsers);

  const shiftCreateLoading = useSelector(selectFacilityShiftCreateLoading);
  const shiftCreateData = useSelector(selectFacilityShiftCreateData);

  const shiftUpdateLoading = useSelector(selectFacilityShiftUpdateLoading);

  const shiftDetailsData = useSelector(selectShiftDetailsData);
  const shiftDetailsLoading = useSelector(selectShiftDetailsLoading);

  const facilityId = useSelector(selectFacilityId);
  const { data: userProfile } = useSelector(selectUserProfile);
  const isOrgAdmin = userProfile?.user_type === 'org_admin';

  const [alert, setAlert] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [showWorkerSuggestions, setShowWorkerSuggestions] = useState<boolean>(false);
  const [lastShfit, setLastShift] = useState<boolean>(false);
  const [shiftDetails, setShiftDetails] = useState<{ date: string; role: string; count: string } | null>(null);

  // --- SETUP REACT HOOK FORM ---
  const { control, handleSubmit, watch, formState: { errors }, reset, setValue } = useForm<IFormInput>({
    resolver: yupResolver(schema) as any,
    defaultValues: defaultValues,
    context: { isOrgAdmin }
  });

  const isLocked = React.useMemo(() => {
    if (!shiftDetailsData) return false;
    const lockedStatuses = ['assigned', 'partially_assigned', 'started', 'partially_started', 'completed', 'partially_completed', 'approval_pending', 'partially_approval_pending'];
    return lockedStatuses.includes(shiftDetailsData.status?.toLowerCase());
  }, [shiftDetailsData]);

  // Handle route params: Fetch details for update or Reset for create
  useEffect(() => {
    if (routeShiftId) {
      setShiftId(parseInt(routeShiftId));
      dispatch(fetchShiftDetails(parseInt(routeShiftId)));
    } else {
      setShiftId(null);
      reset(defaultValues);
    }
  }, [dispatch, routeShiftId, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "shiftSlots"
  });

  // Populate Form when shiftDetailsData is available
  useEffect(() => {
    if (shiftDetailsData && routeShiftId) {
      const startDate = dayjs(shiftDetailsData.start_date_time);
      const endDate = dayjs(shiftDetailsData.end_date_time);

      setValue('shiftSlots.0.startDate', startDate);
      setValue('shiftSlots.0.endDate', endDate);
      setValue('shiftSlots.0.startTime', startDate);
      setValue('shiftSlots.0.endTime', endDate);
      setValue('shiftSlots.0.isSameDay', startDate.isSame(endDate, 'day'));

      // Note: position needs the full OptionType object.
      // We now have position_role_id and position_role (name) from the API.
      if (shiftDetailsData.position_role_id && shiftDetailsData.position_role) {
        setValue('position', {
          id: shiftDetailsData.position_role_id,
          name: shiftDetailsData.position_role
        });
      }

      setValue('workerCount', shiftDetailsData.workers_required);
      setValue('experience', shiftDetailsData.experience_required.toString());
      // set appointMember. API response: "attendance_manager": { email: "...", ... }
      // We need to map attendance_manager back to an ID or if the API provides it.
      if (shiftDetailsData.attendance_manager && facilityUsers) {
        const managerEmail = shiftDetailsData.attendance_manager.email;
        const foundUser = facilityUsers.find(u => u.user.email === managerEmail);
        if (foundUser) {
          setValue('appointMember', foundUser.user.user_id.toString());
        }
      }
      setValue('notes', shiftDetailsData.additional_notes);
      setValue('isSleepover', shiftDetailsData.is_sleepover || false);
      setValue('label', shiftDetailsData.label || '');
      setValue('isMultiShift', false); // Update mode usually single shift?
    }
  }, [shiftDetailsData, routeShiftId, setValue, facilityUsers]);

  useEffect(() => {
    console.log('Shift Created Successfully:', shiftCreateData);
    if (lastShfit && shiftCreateData && shiftCreateData.shift_id) {
      setShiftId(shiftCreateData.shift_id);
      setShiftDetails({
        date: dayjs(shiftCreateData.start_date_time).format('DD/MM/YYYY'),
        role: watch('position')?.name || '',
        count: `1/${watch('workerCount')}`
      });
      setShowWorkerSuggestions(true);
    }
  }, [shiftCreateData, lastShfit, watch]);

  const isMultiShift = watch('isMultiShift');
  const watchedSlots = watch('shiftSlots');

  useEffect(() => {
    watchedSlots?.forEach((slot, index) => {
      if (slot.isSameDay && slot.startDate && (!slot.endDate || !slot.endDate.isSame(slot.startDate, 'day'))) {
        setValue(`shiftSlots.${index}.endDate` as any, slot.startDate);
      }
      
      // Update selectedWorkers array length if workerCount changes
      const count = Number(watch('workerCount')) || 0;
      const currentWorkers = slot.selectedWorkers || [];
      if (isPastSlot(slot)) {
        if (currentWorkers.length !== count) {
          if (currentWorkers.length < count) {
            setValue(`shiftSlots.${index}.selectedWorkers` as any, [...currentWorkers, ...Array(count - currentWorkers.length).fill(null)]);
          } else {
            setValue(`shiftSlots.${index}.selectedWorkers` as any, currentWorkers.slice(0, count));
          }
        }
      } else if (currentWorkers.length > 0) {
        setValue(`shiftSlots.${index}.selectedWorkers` as any, []);
      }
    });
  }, [watchedSlots, setValue, watch, isPastSlot]);

  const selectedFacilityWatch = watch('selectedFacility');

  useEffect(() => {
    const idToFetch = isOrgAdmin ? Number(selectedFacilityWatch?.id) : undefined;
    dispatch(fetchFacilityUsers(idToFetch));
  }, [dispatch, isOrgAdmin, selectedFacilityWatch]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showWarningPopup) {
      timer = setTimeout(() => {
        setShowWarningPopup(false);
      }, 6000);
    }
    return () => clearTimeout(timer);
  }, [showWarningPopup]);


  // --- LOGIC: Handle MultiShift Toggle ---
  const currentSlots = watch('shiftSlots');
  useEffect(() => {
    // 1. If checked and we only have the default slot, ADD Slot 2 immediately
    if (isMultiShift && fields.length === 1) {
      const lastSlot = currentSlots[0];
      append({ 
        startDate: lastSlot?.startDate || null, 
        endDate: lastSlot?.endDate || null, 
        startTime: null, 
        endTime: null,
        isSameDay: lastSlot?.isSameDay ?? true,
        selectedWorkers: []
      });
    }

    // 2. If unchecked and we have extra slots, REMOVE them (reset to just 1)
    if (!isMultiShift && fields.length > 1) {
      const indexesToRemove = Array.from({ length: fields.length - 1 }, (_, i) => i + 1);
      remove(indexesToRemove);
    }
  }, [isMultiShift, fields.length, append, remove, currentSlots]);

  const fetchRoleOptions = useCallback(
    async (search: string, page: number): Promise<FetchResponse<OptionType>> => {
      try {
        const response: any = await dispatch(fetchRoleList({
          page,
          pageSize: 20,
          search: search,
        })).unwrap();

        // Standard paginated response usually has 'results', but role search might return 'data' based on IRoleList
        const results = response.results || response.data || [];

        return {
          options: results.map((r: IRole) => ({
            id: r.role_id,
            name: r.role_name,
          })),
          hasMore: !!response.next,
        };
      } catch (error) {
        return { options: [], hasMore: false };
      }
    },
    [dispatch]
  );

  const fetchWorkerCountOptions = useCallback(
    async (search: string, page: number): Promise<FetchResponse<OptionType>> => {
      const options = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => ({ id: n, name: n.toString() }));
      return { options: options.filter(o => o.name.includes(search)), hasMore: false };
    },
    []
  );

  const fetchMemberOptions = useCallback(
    async (search: string, page: number): Promise<FetchResponse<OptionType>> => {
      if (!facilityUsers) return { options: [], hasMore: false };
      const filtered = facilityUsers.filter(u =>
        `${u.user.first_name} ${u.user.last_name}`.toLowerCase().includes(search.toLowerCase())
      ).map(u => ({
        id: u.user.user_id,
        name: `${u.user.first_name} ${u.user.last_name}`,
      }));
      return { options: filtered, hasMore: false };
    },
    [facilityUsers]
  );

  const fetchFacilityOptions = useCallback(
    async (search: string, page: number): Promise<FetchResponse<OptionType>> => {
      const response: any = await dispatch(fetchFacilityList({
        page,
        pageSize: 20,
        search: search,
      })).unwrap();
      return {
        options: response.results.map((f: any) => ({
          id: f.facility_id,
          name: f.facility_name
        })),
        hasMore: response.next !== null
      };
    },
    [dispatch]
  );

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    const targetFacilityId = isOrgAdmin ? Number(data.selectedFacility?.id) : facilityId;
    if (!targetFacilityId) {
      setAlert({ message: 'Facility ID is missing. Please select a facility.', severity: 'error' });
      return;
    }
    console.log('Form Data Submitted:', data);

    // Check for past dates and worker assignment per slot
    let workerMissing = false;
    data.shiftSlots.forEach(slot => {
      if (isPastSlot(slot)) {
        const filledCount = (slot.selectedWorkers || []).filter(w => w !== null).length;
        if (filledCount < Number(data.workerCount)) {
          workerMissing = true;
        }
      }
    });

    if (workerMissing) {
      // This case should be unreachable due to schema validation blocking past shifts
      setAlert({ message: "Please select workers for all past shifts.", severity: "error" });
      return;
    }

    // Normalization logic
    const payloads = data.shiftSlots.map((slot) => {
      const startDateTime = slot.startDate
        ?.hour(slot.startTime?.hour() || 0)
        .minute(slot.startTime?.minute() || 0)
        .second(0);

      const endDateTime = slot.endDate
        ?.hour(slot.endTime?.hour() || 0)
        .minute(slot.endTime?.minute() || 0)
        .second(0);

      return {
        facility_id: targetFacilityId,
        start_date_time: startDateTime?.format('YYYY-MM-DD HH:mm:ss[Z]') || '',
        end_date_time: endDateTime?.format('YYYY-MM-DD HH:mm:ss[Z]') || '',
        position_role: Number(data.position?.id) || 0,
        workers_required: Number(data.workerCount),
        experience_required: Number(data.experience) || 0,
        attendance_manager: Number(data.appointMember),
        additional_notes: data.notes,
        is_sleepover: data.isSleepover,
        label: data.label,
        assigned_workers: isPastSlot(slot) ? (slot.selectedWorkers || []).filter(w => w !== null).map(w => Number(w?.id)) : undefined
      };
    });

    if (routeShiftId) {
      // UPDATE MODE (Single shift usually)
      if (payloads.length > 0) {
        try {
          const res: any = await dispatch(updateFacilityShift({ id: parseInt(routeShiftId), data: payloads[0] })).unwrap();
          const successMsg = res?.message || res?.data?.message || res?.detail || 'Shift updated successfully!';
          if (successMsg !== 'Shift hours is higher than normal shift, but your request submitted to our agency!') {
            setAlert({ message: successMsg, severity: 'success' });
            setTimeout(() => {
              navigate('/facility/shifts/');
            }, 1000);
          } else {
            setWarningMessage(successMsg);
            setShowWarningPopup(true);
          }
        } catch (err: any) {
          const errMsg = typeof err === 'string' ? err : (err?.message || err?.detail || err?.data?.message || 'Error updating shift');
          setAlert({ message: `Error updating shift: ${errMsg}`, severity: 'error' });
        }
      }
    } else {
      // CREATE MODE
      setShiftId(null);
      let shiftCount = payloads.length;
      let count = 1;
      for (let i = 0; i < payloads.length; i++) {
        const payload = payloads[i];
        if (count === shiftCount) setLastShift(true);
        try {
          const res: any = await dispatch(createFacilityShift(payload)).unwrap();
          if (count === shiftCount) {
             const successMsg = res?.message || res?.data?.message || res?.detail || 'Shift created successfully!';
             if (successMsg !== 'Shift hours is higher than normal shift, but your request submitted to our agency!') {
               setAlert({ message: successMsg, severity: 'success' });
             } else {
               setWarningMessage(successMsg);
               setShowWarningPopup(true);
             }
             reset();
          }
        } catch (err: any) {
          const errMsg = typeof err === 'string' ? err : (err?.message || err?.detail || err?.data?.message || 'Unknown error');
          setAlert({ message: `Error creating shift: ${errMsg}`, severity: 'error' });
        }
        count += 1;
      }
    }
  };

  const onError = (errors: any) => {
    console.log('Form Validation Errors:', errors);
    setAlert({ message: 'Please fill in all required fields correctly.', severity: 'error' });
  };

  return (
    <PageWrapper>
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit, onError)}>

          <Box sx={{ 
            pt: 2, 
            pb: 2, 
            mb: 2, 
            borderBottom: '1px solid #eee' 
          }}>
            <HeaderRow>
              <Typography variant="h5" style={{ fontWeight: 'bold', color: '#222' }}>
                {routeShiftId ? 'Update Requirement' : 'Post a New Requirement'}
              </Typography>
            </HeaderRow>

            <Grid container spacing={3}>
              {/* Row: Facility Selector (Only if Org Admin) */}
              {isOrgAdmin && (
                <Grid size={12}>
                  <SectionLabel>Facility Name</SectionLabel>
                  <Controller
                    name="selectedFacility"
                    control={control}
                    render={({ field }) => (
                      <>
                        <PaginatedSearchSelect
                          fetchOptions={fetchFacilityOptions}
                          getOptionLabel="name"
                          value={field.value || null}
                          onChange={field.onChange}
                          placeholder="Search & Select Facility"
                          size='small'
                          fullWidth
                          disabled={isLocked}
                        />
                        {errors.selectedFacility && (
                          <FormHelperText error>{(errors.selectedFacility as any)?.message}</FormHelperText>
                        )}
                      </>
                    )}
                  />
                </Grid>
              )}

              {/* Row 1: Position & Worker Count */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionLabel>Position</SectionLabel>
                <Controller
                  name="position"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <PaginatedSearchSelect
                        fetchOptions={fetchRoleOptions}
                        getOptionLabel="name"
                        value={value || null}
                        onChange={onChange}
                        placeholder="-Select a position-"
                        size='small'
                        fullWidth
                        disabled={isLocked}
                      />
                      {errors.position && (
                        <FormHelperText error>{(errors.position as any)?.message}</FormHelperText>
                      )}
                    </>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionLabel>No of Workers Required</SectionLabel>
                <Controller
                  name="workerCount"
                  control={control}
                  render={({ field }) => (
                    <>
                      <PaginatedSearchSelect
                        fetchOptions={fetchWorkerCountOptions}
                        getOptionLabel="name"
                        value={field.value ? { id: Number(field.value), name: field.value.toString() } : null}
                        onChange={(newValue: any) => field.onChange(newValue ? newValue.id : '')}
                        placeholder="-Select number of workers-"
                        size='small'
                        fullWidth
                        disabled={isLocked}
                      />
                      {errors.workerCount && <FormHelperText error>{errors.workerCount.message}</FormHelperText>}
                    </>
                  )}
                />
              </Grid>

              {/* Row 2: Experience & Appoint Member */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionLabel>Experience</SectionLabel>
                <Controller
                  name="experience"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      placeholder="Enter experience needed"
                      disabled={isLocked}
                      error={!!errors.experience}
                      helperText={errors.experience?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SectionLabel>Appoint Member</SectionLabel>
                <Controller
                  name="appointMember"
                  control={control}
                  render={({ field }) => {
                    const selectedOption = facilityUsers && field.value
                      ? facilityUsers
                        .filter(u => u.user.user_id.toString() === field.value.toString())
                        .map(u => ({ id: u.user.user_id, name: `${u.user.first_name} ${u.user.last_name}` }))[0]
                      : null;

                    return (
                      <>
                        <PaginatedSearchSelect
                          fetchOptions={fetchMemberOptions}
                          getOptionLabel="name"
                          value={selectedOption || null}
                          onChange={(newValue: any) => field.onChange(newValue ? newValue.id : '')}
                          placeholder={(!isOrgAdmin || selectedFacilityWatch) ? "- Select appoint member -" : "Select Facility to load members"}
                          size='small'
                          fullWidth
                          disabled={(isOrgAdmin && !selectedFacilityWatch) || isLocked}
                        />
                        {errors.appointMember && <FormHelperText error>{errors.appointMember.message}</FormHelperText>}
                      </>
                    );
                  }}
                />
              </Grid>

              <Grid size={12}>
                <SectionLabel>Additional Notes</SectionLabel>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Add any notes..."
                      error={!!errors.notes}
                      helperText={errors.notes?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={12}>
                <SectionLabel>Label</SectionLabel>
                <Controller
                  name="label"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      placeholder="Enter label (e.g. Area, Floor, etc.)"
                      error={!!errors.label}
                      helperText={errors.label?.message}
                    />
                  )}
                />
              </Grid>

              {/* Row 3: Sleepover & MultiShift Checkboxes */}
              <Grid size={12}>
                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Controller
                    name="isSleepover"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        sx={{ mt: 1, mb: 0 }}
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value}
                            disabled={isLocked}
                            sx={{ color: THEME_COLOR, '&.Mui-checked': { color: THEME_COLOR } }}
                          />
                        }
                        label={<Typography sx={{ fontWeight: 500, color: '#555' }}>Sleepover Shift</Typography>}
                      />
                    )}
                  />
                  {!routeShiftId && (
                    <Controller
                      name="isMultiShift"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          sx={{ mt: 1, mb: 0 }}
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              sx={{ color: THEME_COLOR, '&.Mui-checked': { color: THEME_COLOR } }}
                            />
                          }
                          label={<Typography sx={{ fontWeight: 500, color: '#555' }}>Create Multiple Shifts</Typography>}
                        />
                      )}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>

            {/* Row 4: Slot 1 Title */}
            {isMultiShift && (
              <Grid size={12} sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#555', fontWeight: 600 }}>
                  Shift Slot 1
                </Typography>
              </Grid>
            )}

            {/* Row 5: Slot 1 Dates */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                <SectionLabel sx={{ mb: 0 }}>Start Date</SectionLabel>
              </Box>
              <Controller
                name="shiftSlots.0.startDate"
                control={control}
                render={({ field }) => (
                  <Box sx={{ position: 'relative' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker format="DD/MM/YYYY"
                        {...field}
                        disablePast
                        disabled={isLocked}
                        value={field.value || null}
                        onChange={(newValue) => {
                          field.onChange(newValue);
                          if (watch('shiftSlots.0.isSameDay') && newValue) {
                            setValue('shiftSlots.0.endDate', newValue, { shouldValidate: true });
                          }
                        }}
                        sx={{ width: '100%' }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: !!(errors.shiftSlots as any)?.[0]?.startDate,
                            helperText: (errors.shiftSlots as any)?.[0]?.startDate?.message
                          },
                          popper: {
                            disablePortal: true,
                            sx: {
                              position: 'absolute',
                              top: '100%',
                              left: '0',
                              transform: 'none !important',
                              mt: '4px',
                              zIndex: 1300,
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ height: '40px' }}>
                <SectionLabel sx={{ mb: 0 }}>End Date</SectionLabel>
                <Controller
                  name="shiftSlots.0.isSameDay"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={
                        <Checkbox
                          {...field}
                          size="small"
                          checked={field.value}
                          disabled={isLocked}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            field.onChange(checked);
                            if (checked) {
                              const startDate = watch('shiftSlots.0.startDate');
                              if (startDate) {
                                setValue('shiftSlots.0.endDate', startDate, { shouldValidate: true });
                              }
                            }
                          }}
                          sx={{ 
                            p: 0.5,
                            color: THEME_COLOR, 
                            '&.Mui-checked': { color: THEME_COLOR } 
                          }}
                        />
                      }
                      label={<Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#555' }}>Same day</Typography>}
                    />
                  )}
                />
              </Box>
              <Controller
                name="shiftSlots.0.endDate"
                control={control}
                render={({ field }) => (
                  <Box sx={{ position: 'relative' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker format="DD/MM/YYYY"
                        {...field}
                        disablePast
                        value={field.value || null}
                        disabled={isLocked || watch('shiftSlots.0.isSameDay')}
                        sx={{ width: '100%' }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: !!(errors.shiftSlots as any)?.[0]?.endDate,
                            helperText: (errors.shiftSlots as any)?.[0]?.endDate?.message
                          },
                          popper: {
                            disablePortal: true,
                            sx: {
                              position: 'absolute',
                              top: '100%',
                              left: '0',
                              transform: 'none !important',
                              mt: '4px',
                              zIndex: 1300,
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                )}
              />
            </Grid>

            {/* Row 6: Slot 1 Times */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <SectionLabel>Shift Starting Time</SectionLabel>
              <Controller
                name="shiftSlots.0.startTime"
                control={control}
                render={({ field }) => (
                  <Box sx={{ position: 'relative' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        {...field}
                        disabled={isLocked}
                        minTime={watch('shiftSlots.0.startDate') && dayjs().isSame(watch('shiftSlots.0.startDate'), 'day') ? dayjs() : undefined}
                        value={field.value || null}
                        sx={{ width: '100%' }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: !!(errors.shiftSlots as any)?.[0]?.startTime,
                            helperText: (errors.shiftSlots as any)?.[0]?.startTime?.message
                          },
                          popper: {
                            disablePortal: true,
                            sx: {
                              position: 'absolute',
                              top: '100%',
                              left: '0',
                              transform: 'none !important',
                              mt: '4px',
                              zIndex: 1300,
                            }
                          }
                        }}
                        ampm={true}
                      />
                    </LocalizationProvider>
                  </Box>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SectionLabel>Shift Ending Time</SectionLabel>
              <Controller
                name="shiftSlots.0.endTime"
                control={control}
                render={({ field }) => (
                  <Box sx={{ position: 'relative' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        {...field}
                        disabled={isLocked}
                        minTime={
                          (watch('shiftSlots.0.endDate') && watch('shiftSlots.0.startDate') && dayjs(watch('shiftSlots.0.endDate')).isSame(watch('shiftSlots.0.startDate'), 'day') && watch('shiftSlots.0.startTime'))
                            ? (watch('shiftSlots.0.startTime') || undefined)
                            : (watch('shiftSlots.0.endDate') && dayjs(watch('shiftSlots.0.endDate')).isSame(dayjs(), 'day') ? dayjs() : undefined)
                        }
                        value={field.value || null}
                        sx={{ width: '100%' }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: !!(errors.shiftSlots as any)?.[0]?.endTime,
                            helperText: (errors.shiftSlots as any)?.[0]?.endTime?.message
                          },
                          popper: {
                            disablePortal: true,
                            sx: {
                              position: 'absolute',
                              top: '100%',
                              left: '0',
                              transform: 'none !important',
                              mt: '4px',
                              zIndex: 1300,
                            }
                          }
                        }}
                        ampm={true}
                      />
                    </LocalizationProvider>
                  </Box>
                )}
              />
            </Grid>

            {/* Row 7: Slot 1 Workers (if past) */}
            {/* Removed Past Shift worker selection as past shifts are no longer permitted on this page */}

            {/* ================================================================= */}
            {/* Row 7: ADDITIONAL SLOTS (Slot 2, 3...)                            */}
            {/* ================================================================= */}
            {isMultiShift && fields.map((field, index) => {
              if (index === 0) return null; // Skip first slot

              return (
                <React.Fragment key={field.id}>
                  <Grid size={12} sx={{ mt: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" sx={{ color: '#555', fontWeight: 600 }}>
                        Shift Slot {index + 1}
                      </Typography>
                      <IconButton onClick={() => remove(index)} size="small" color="error">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                      <SectionLabel sx={{ mb: 0 }}>Start Date</SectionLabel>
                    </Box>
                    <Controller
                      name={`shiftSlots.${index}.startDate`}
                      control={control}
                      render={({ field }) => (
                        <Box sx={{ position: 'relative' }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker format="DD/MM/YYYY"
                              {...field}
                              disablePast
                              disabled={isLocked}
                              value={field.value || null}
                              onChange={(newValue) => {
                                field.onChange(newValue);
                                if (watch(`shiftSlots.${index}.isSameDay` as any) && newValue) {
                                  setValue(`shiftSlots.${index}.endDate` as any, newValue, { shouldValidate: true });
                                }
                              }}
                              sx={{ width: '100%' }}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  error: !!(errors.shiftSlots as any)?.[index]?.startDate,
                                  helperText: (errors.shiftSlots as any)?.[index]?.startDate?.message
                                },
                                popper: {
                                  disablePortal: true,
                                  sx: {
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    transform: 'none !important',
                                    mt: '4px',
                                    zIndex: 1300,
                                  }
                                }
                              }}
                            />
                          </LocalizationProvider>
                        </Box>
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ height: '40px' }}>
                      <SectionLabel sx={{ mb: 0 }}>End Date</SectionLabel>
                      <Controller
                        name={`shiftSlots.${index}.isSameDay`}
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            sx={{ mr: 0 }}
                            control={
                              <Checkbox
                                {...field}
                                size="small"
                                checked={field.value}
                                disabled={isLocked}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  field.onChange(checked);
                                  if (checked) {
                                    const startDate = watch(`shiftSlots.${index}.startDate` as any);
                                    if (startDate) {
                                      setValue(`shiftSlots.${index}.endDate` as any, startDate, { shouldValidate: true });
                                    }
                                  }
                                }}
                                sx={{ 
                                  p: 0.5,
                                  color: THEME_COLOR, 
                                  '&.Mui-checked': { color: THEME_COLOR } 
                                }}
                              />
                            }
                            label={<Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#555' }}>Same day</Typography>}
                          />
                        )}
                      />
                    </Box>
                    <Controller
                      name={`shiftSlots.${index}.endDate`}
                      control={control}
                      render={({ field }) => (
                        <Box sx={{ position: 'relative' }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker format="DD/MM/YYYY"
                                {...field}
                                disablePast
                                value={field.value || null}
                                disabled={isLocked || watch(`shiftSlots.${index}.isSameDay` as any)}
                                sx={{ width: '100%' }}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  error: !!(errors.shiftSlots as any)?.[index]?.endDate,
                                  helperText: (errors.shiftSlots as any)?.[index]?.endDate?.message
                                },
                                popper: {
                                  disablePortal: true,
                                  sx: {
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    transform: 'none !important',
                                    mt: '4px',
                                    zIndex: 1300,
                                  }
                                }
                              }}
                            />
                          </LocalizationProvider>
                        </Box>
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <SectionLabel>Shift Starting Time</SectionLabel>
                    <Controller
                      name={`shiftSlots.${index}.startTime`}
                      control={control}
                      render={({ field }) => (
                        <Box sx={{ position: 'relative' }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                              {...field}
                              disabled={isLocked}
                              minTime={watch(`shiftSlots.${index}.startDate` as any) && dayjs().isSame(watch(`shiftSlots.${index}.startDate` as any), 'day') ? dayjs() : undefined}
                              value={field.value || null}
                              sx={{ width: '100%' }}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  error: !!(errors.shiftSlots as any)?.[index]?.startTime,
                                  helperText: (errors.shiftSlots as any)?.[index]?.startTime?.message
                                },
                                popper: {
                                  disablePortal: true,
                                  sx: {
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    transform: 'none !important',
                                    mt: '4px',
                                    zIndex: 1300,
                                  }
                                }
                              }}
                              ampm={true}
                            />
                          </LocalizationProvider>
                        </Box>
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <SectionLabel>Shift Ending Time</SectionLabel>
                    <Controller
                      name={`shiftSlots.${index}.endTime`}
                      control={control}
                      render={({ field }) => (
                        <Box sx={{ position: 'relative' }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                              {...field}
                              disabled={isLocked}
                              minTime={
                                (watch(`shiftSlots.${index}.endDate` as any) && watch(`shiftSlots.${index}.startDate` as any) && dayjs(watch(`shiftSlots.${index}.endDate` as any)).isSame(watch(`shiftSlots.${index}.startDate` as any), 'day') && watch(`shiftSlots.${index}.startTime` as any))
                                  ? (watch(`shiftSlots.${index}.startTime` as any) || undefined)
                                  : (watch(`shiftSlots.${index}.endDate` as any) && dayjs(watch(`shiftSlots.${index}.endDate` as any)).isSame(dayjs(), 'day') ? dayjs() : undefined)
                              }
                              value={field.value || null}
                              sx={{ width: '100%' }}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  error: !!(errors.shiftSlots as any)?.[index]?.endTime,
                                  helperText: (errors.shiftSlots as any)?.[index]?.endTime?.message
                                },
                                popper: {
                                  disablePortal: true,
                                  sx: {
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    transform: 'none !important',
                                    mt: '4px',
                                    zIndex: 1300,
                                  }
                                }
                              }}
                              ampm={true}
                            />
                          </LocalizationProvider>
                        </Box>
                      )}
                    />
                  </Grid>

                  {/* Multi Slot Workers (if past) */}
                  {/* Removed Past Shift worker selection for additional slots */}
                </React.Fragment>
              );
            })}

            {isMultiShift && (
              <Grid size={12}>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const lastSlot = currentSlots[currentSlots.length - 1];
                      append({ 
                        startDate: lastSlot?.startDate || null, 
                        endDate: lastSlot?.endDate || null, 
                        startTime: null, 
                        endTime: null,
                        isSameDay: lastSlot?.isSameDay ?? true,
                        selectedWorkers: []
                      });
                    }}
                    sx={{
                      color: THEME_COLOR,
                      fontWeight: 'bold',
                      textTransform: 'none',
                      '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
                    }}
                  >
                    Add More Slots
                  </Button>
                </Box>
              </Grid>
            )}

            {/* Footer Actions */}
            <Grid size={12}>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <CancelButton variant="outlined" onClick={() => reset(defaultValues)}>Cancel</CancelButton>
                <PrimaryButton variant="contained" type="submit" disabled={shiftCreateLoading || shiftUpdateLoading}>
                  {shiftCreateLoading || shiftUpdateLoading ? 'Processing...' : (routeShiftId ? 'Update Shift' : 'Post New Shift')}
                </PrimaryButton>
              </Box>
            </Grid>
          </Grid>
        </form>
        {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
        {showWarningPopup && (
          <AlertPopup
            open={showWarningPopup}
            message={warningMessage}
            severity="warning"
            onClose={() => {
              setShowWarningPopup(false);
              navigate('/facility-shift-requests');
            }}
            actionButton={
              <Button color="inherit" size="small" onClick={() => {
                setShowWarningPopup(false);
                navigate('/facility-shift-requests');
              }}>
                View Requests
              </Button>
            }
          />
        )}
      </FormContainer>
      <FacilityShiftWorkerSuggestions
        open={showWorkerSuggestions}
        onClose={() => setShowWorkerSuggestions(false)}
        shiftId={shiftId}
        shiftDetails={shiftDetails}
      />
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={shiftDetailsLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </PageWrapper>
  );
};

export default FacilityShiftCreate;